"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Phone, Sparkles, Loader, Send } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
export default function LetsStartWorkingOnYourBrand() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="76048" id="זקוקים-לייעוץ-אישי" className="dark bg-background text-foreground relative overflow-hidden py-24 lg:py-32" dir="rtl">
      <div className="absolute top-0 right-0 -translate-y-1/4 translate-x-1/4 w-[500px] h-[500px] bg-chart-1/20 rounded-lg blur-[120px] pointer-events-none" />
      <div className="absolute bottom-0 left-0 translate-y-1/4 -translate-x-1/4 w-[500px] h-[500px] bg-chart-2/20 rounded-lg blur-[120px] pointer-events-none" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] bg-primary/5 rounded-lg blur-[100px] pointer-events-none" />

      <div className="container mx-auto px-4 relative z-10">
        <div ref={ref} className={`max-w-5xl mx-auto transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          <Card className="bg-card/40 backdrop-blur-xl border-border/50 shadow-2xl overflow-hidden relative">
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-chart-1 via-chart-2 to-chart-3 opacity-50" />
            
            <CardContent className="p-8 md:p-16 flex flex-col items-center text-center gap-8">
              <div className="inline-flex items-center justify-center p-3 bg-secondary/10 rounded-lg mb-4">
                <Sparkles className="size-8 text-secondary" />
              </div>
              
              <div className="space-y-4 max-w-3xl">
                <h2 className="text-4xl md:text-5xl lg:text-6xl font-default font-bold tracking-tight text-foreground">
                  זקוקים לייעוץ אישי ומיידי?
                </h2>
                <p className="text-lg md:text-xl text-muted-foreground leading-relaxed font-default">
                  המומחים שלנו בסיעוד מיידי זמינים עבורכם להתאמה מדויקת של מטפל סיעודי ישראלי תוך 5 שעות בלבד. השאירו פרטים עכשיו וקבלו ליווי אישי ומקצועי לכל אורך הדרך.
                </p>
              </div>

              <div className="w-full max-w-xl mt-4">
                <FormProvider formId="consultation-request-footer" sectionName="LetsStartWorkingOnYourBrand" defaultValues={{
                name: "",
                phone: "",
                email: "",
                message: ""
              }} validationRules={{
                name: {
                  required: true,
                  minLength: 2
                },
                phone: {
                  required: true,
                  pattern: "phone"
                },
                email: {
                  pattern: "email"
                }
              }} submitText="שלחו בקשה לייעוץ" successMessage="תודה! פנייתך התקבלה. נציג יחזור אליך תוך זמן קצר.">
                  <ConsultationForm />
                </FormProvider>
              </div>

              <div className="flex flex-col sm:flex-row items-center gap-4 w-full sm:w-auto mt-4">
                <Button asChild variant="outline" size="lg" className="w-full sm:w-auto text-base h-14 px-8 bg-background/50 hover:bg-accent hover:text-accent-foreground border-border/50 font-default">
                  <Link to="tel:03-1234567">
                    <Phone className="ml-2 size-5" />
                    התקשרו עכשיו
                  </Link>
                </Button>
              </div>

              <div className="mt-8 pt-8 border-t border-border/30 w-full max-w-md flex flex-col items-center gap-2 text-sm text-muted-foreground font-default">
                <p>זמינים עבורכם גם במייל:</p>
                <Link to="mailto:office@siudy.co.il" className="text-secondary hover:underline font-default font-medium transition-colors">
                  office@siudy.co.il
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>;
}
function ConsultationForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-8 bg-secondary/10 border border-secondary/20 rounded-lg text-center space-y-4 animate-in fade-in zoom-in duration-300">
        <p className="text-xl font-bold text-foreground">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="font-default">
          שליחת פנייה נוספת
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-4 text-right">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormField control={control} name="name" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-default">שם מלא</FormLabel>
              <FormControl>
                <Input placeholder="ישראל ישראלי" className="bg-background/50 border-border/50 h-12 font-default" {...field} />
              </FormControl>
              <FormMessage className="font-default" />
            </FormItem>} />

        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-default">טלפון</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="050-0000000" className="bg-background/50 border-border/50 h-12 font-default" {...field} />
              </FormControl>
              <FormMessage className="font-default" />
            </FormItem>} />
      </div>

      <FormField control={control} name="email" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-default">אימייל (אופציונלי)</FormLabel>
            <FormControl>
              <Input type="email" placeholder="your@email.com" className="bg-background/50 border-border/50 h-12 font-default" {...field} />
            </FormControl>
            <FormMessage className="font-default" />
          </FormItem>} />

      <FormField control={control} name="message" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-default">איך נוכל לעזור?</FormLabel>
            <FormControl>
              <Textarea placeholder="ספרו לנו בקצרה על הצורך שלכם..." className="bg-background/50 border-border/50 min-h-[100px] font-default" {...field} />
            </FormControl>
            <FormMessage className="font-default" />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
          <p className="text-destructive text-sm font-default">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-14 text-lg font-bold bg-secondary hover:bg-secondary/90 text-secondary-foreground shadow-lg shadow-secondary/20 transition-all active:scale-[0.98] font-default">
        {isSubmitting ? <>
            <Loader className="ml-2 size-5 animate-spin" />
            שולח...
          </> : <>
            <Send className="ml-2 size-5" />
            השאירו פרטים לייעוץ
          </>}
      </Button>
    </Form>;
}